import { faArrowRight, faBinoculars, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Debug from '@/components/Debug/Debug';
import FieldValue from '@/components/Debug/FieldValue';
import RemitServiceLineCard from '@/components/RemitServiceLineCard';
import RenderHTML from '@/components/RenderHTML';
import {
  InsurancePolicyTimelineEvent,
  TimelinePolicy
} from '@/repositories/InsurancePolicyTimelineRepository';
import type { RemitServiceLine } from '@/types/billing';
import type { ExternalPolicyCoverage } from '@/types/externalVerification';
import { Button, Card, CopyContent, Pill } from '@/ui';
import { currencyFormatter, currencyInCentsFormatter } from '@/utils/currencies';
import { formatISOStringToReadableDate } from '@/utils/dates';
import usFormatDateString from '@/utils/usFormatDateString';

import {
  derivePointInTimeDrift,
  deriveDisplayCarrier,
  deriveEventDisplay,
  formatPlanDate,
  groupCoverages,
  PLAN_DATE_FIELDS
} from '../utils';

import EventField from './EventField';
import EventPanel from './EventPanel';
import PolicyBanner, { policyRailColor } from './PolicyBanner';

interface TimelineRowProps {
  events: InsurancePolicyTimelineEvent[];
  policies: TimelinePolicy[];
  sortOrder?: 'asc' | 'desc';
  focusedEventId?: string | null;
  onFocusEvent?: (eventId: string | null) => void;
}

interface PolicyRun {
  key: string;
  policyId: string;
  items: { event: InsurancePolicyTimelineEvent; index: number }[];
}

// Uncapped, a multi-year gap pushes the next row thousands of pixels down.  Past a
// point the distance stops meaning anything, so a label carries it instead.
const MAX_GAP_SPACING_REM = 4;
const GAP_LABEL_DAYS = 30;

const daysBetween = (a: string, b: string) =>
  Math.abs((new Date(a).getTime() - new Date(b).getTime()) / (1000 * 60 * 60 * 24));

const monthLabel = (timestamp: string) => {
  const at = new Date(timestamp);
  const month = at.toLocaleDateString('en-US', { month: 'short' });

  return `${month} - ${at.getFullYear()}`;
};

const CALENDAR_DATE_FIELDS: Record<string, string> = {
  plan_start: 'plan_start_date',
  plan_end: 'plan_end_date'
};

const calendarDateOf = (event: InsurancePolicyTimelineEvent): string | undefined => {
  const field = CALENDAR_DATE_FIELDS[event.event_type];
  return field ? event.data[field] : undefined;
};

const formatEventDay = (event: InsurancePolicyTimelineEvent) => {
  const calendarDate = calendarDateOf(event);
  if (calendarDate) return formatPlanDate(calendarDate);

  return new Date(event.timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatEventTime = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit'
  });

// Neutral phrasing — the list reads either direction depending on sort order.
const formatGap = (days: number): string | null => {
  if (days < GAP_LABEL_DAYS) return null;

  const years = Math.floor(days / 365);
  if (years >= 1) return `${years} year${years === 1 ? '' : 's'}`;

  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'}`;
};

// A link to one event carries the current filters, so it opens the same view.
const eventPermalink = (eventId: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set('event', eventId);

  return `${window.location.origin}${window.location.pathname}?${params}`;
};

const TimelineDivider: React.FC<{ label: string }> = ({ label }) => (
  <div className="my-4 flex items-center gap-3 pl-10 text-xs text-gray-500">
    <span className="h-px flex-1 bg-gray-200" />
    <span className="font-medium whitespace-nowrap">{label}</span>
    <span className="h-px flex-1 bg-gray-200" />
  </div>
);

const TimelineRow: React.FC<TimelineRowProps> = ({
  events,
  policies,
  sortOrder = 'desc',
  focusedEventId,
  onFocusEvent
}) => {
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(
    new Set(focusedEventId ? [focusedEventId] : [])
  );

  const hasScrolledToFocused = useRef(false);

  useEffect(() => {
    hasScrolledToFocused.current = false;
  }, [focusedEventId]);

  useEffect(() => {
    if (!focusedEventId) return;

    setExpandedEventIds((current) => new Set(current).add(focusedEventId));
  }, [focusedEventId]);

  // Scroll only once the event both exists — it arrives with the timeline — and has
  // expanded, since expanding changes the height the scroll is centring on.
  useEffect(() => {
    if (hasScrolledToFocused.current) return;
    if (!focusedEventId || !expandedEventIds.has(focusedEventId)) return;

    const target = document.getElementById(`timeline-event-${focusedEventId}`);
    if (!target) return;

    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    hasScrolledToFocused.current = true;
  }, [focusedEventId, expandedEventIds, events]);

  const sortedEvents = useMemo(
    () => (sortOrder === 'desc' ? events : [...events].reverse()),
    [events, sortOrder]
  );

  // Consecutive events for one policy share a rail, so a switch of policy is visible
  // without reading every card.
  const runs = useMemo(() => {
    const result: PolicyRun[] = [];

    sortedEvents.forEach((event, index) => {
      const current = result[result.length - 1];
      if (current && current.policyId === event.policy_id) {
        current.items.push({ event, index });
      } else {
        result.push({
          key: `${event.policy_id}-${event.id}`,
          policyId: event.policy_id,
          items: [{ event, index }]
        });
      }
    });

    return result;
  }, [sortedEvents]);

  // A coverage collapsed into a run's card is reached through the card that shows it.
  const cardIdByEventId = useMemo(() => {
    const cardIds = new Map<string, string>();

    runs.forEach((run) => {
      groupCoverages(run.items.map(({ event }) => event)).forEach((coverages) => {
        coverages.forEach((coverage) => cardIds.set(coverage.id, coverages[0].id));
      });
    });

    return cardIds;
  }, [runs]);

  const renderEventLink = (targetEventId: string | undefined, label: string) => {
    const cardId = targetEventId ? cardIdByEventId.get(targetEventId) : undefined;
    if (!cardId || !onFocusEvent) return null;

    return (
      <Button
        variant="quiet"
        color="secondary"
        size="xs"
        rightIcon={faArrowRight}
        onClick={() => onFocusEvent(cardId)}>
        {label}
      </Button>
    );
  };

  const dividerLabels = useMemo(() => {
    const labels = new Map<string, string>();

    sortedEvents.forEach((event, index) => {
      if (index === 0) return;

      const previous = sortedEvents[index - 1];
      const gapLabel = formatGap(daysBetween(event.timestamp, previous.timestamp));
      const entersNewMonth = monthLabel(event.timestamp) !== monthLabel(previous.timestamp);

      if (!entersNewMonth && !gapLabel) return;

      labels.set(event.id, [monthLabel(event.timestamp), gapLabel].filter(Boolean).join(' · '));
    });

    return labels;
  }, [sortedEvents]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatActionStamp = ({ at, by }: { at: string; by: string | null }) =>
    by ? `${formatISOStringToReadableDate(at)} by ${by}` : formatISOStringToReadableDate(at);

  // Closing the event a link pointed at is the reader moving on, so the highlight goes.
  const toggleExpanded = (eventId: string) => {
    const isCollapsing = expandedEventIds.has(eventId);

    setExpandedEventIds((prev) => {
      const next = new Set(prev);
      if (isCollapsing) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });

    if (isCollapsing && eventId === focusedEventId) {
      onFocusEvent?.(null);
    }
  };

  // Increased base spacing: 1.5rem between all events (was 1rem)
  // Additional spacing: +0.75rem for every 5 days of gap (was 0.5rem)
  const getAdditionalSpacing = (days: number): number =>
    Math.min(Math.floor(days / 5) * 0.75, MAX_GAP_SPACING_REM);

  const renderEventData = (event: InsurancePolicyTimelineEvent) => {
    const data = event.data;
    if (!data || Object.keys(data).length === 0) return null;

    const edge = deriveEventDisplay(event).palette.edge;

    // Special handling for field changes
    if (event.event_type === 'policy_field_change' && data.changes) {
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          {Object.entries(data.changes).map(([fieldName, changeData]: [string, unknown]) => {
            if (
              fieldName === 'insurance_provider' &&
              typeof changeData === 'object' &&
              changeData !== null &&
              'old' in changeData &&
              'new' in changeData &&
              typeof changeData.old === 'object' &&
              changeData.old !== null &&
              'internal_name' in changeData.old &&
              typeof changeData.new === 'object' &&
              changeData.new !== null &&
              'internal_name' in changeData.new
            ) {
              const oldProvider = changeData.old as { internal_name?: string };
              const newProvider = changeData.new as { internal_name?: string };
              return (
                <EventPanel key={fieldName} edge={edge} padding="xs">
                  <div className="text-sm font-medium text-gray-700">Insurance Provider</div>
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    {oldProvider.internal_name && (
                      <span className="rounded bg-red-100 px-2 py-1 text-red-800">
                        {oldProvider.internal_name}
                      </span>
                    )}
                    <span className="text-gray-400">-&gt;</span>
                    {newProvider.internal_name && (
                      <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                        {newProvider.internal_name}
                      </span>
                    )}
                  </div>
                </EventPanel>
              );
            }

            if (Array.isArray(changeData) && changeData.length === 2) {
              const [oldValue, newValue] = changeData;
              const isPlanDate = PLAN_DATE_FIELDS.has(fieldName);
              const hasOldValue =
                oldValue !== null &&
                oldValue !== undefined &&
                oldValue !== 'None' &&
                oldValue !== '';
              const hasNewValue =
                newValue !== null &&
                newValue !== undefined &&
                newValue !== 'None' &&
                newValue !== '';

              return (
                <EventPanel key={fieldName} edge={edge} padding="xs">
                  <div className="text-sm font-medium text-gray-700">
                    {fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    {hasOldValue && (
                      <span className="rounded bg-red-100 px-2 py-1 text-red-800">
                        {isPlanDate ? formatPlanDate(oldValue) : String(oldValue)}
                      </span>
                    )}
                    {hasOldValue && hasNewValue && <span className="text-gray-400">-&gt;</span>}
                    {hasNewValue && (
                      <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                        {isPlanDate ? formatPlanDate(newValue) : String(newValue)}
                      </span>
                    )}
                  </div>
                </EventPanel>
              );
            }

            return null;
          })}
        </div>
      );
    }

    // Handle external policy verification
    if (event.event_type === 'external_policy_verification') {
      const vobLink = renderEventLink(data.insurance_verification_event_id, 'View');

      return (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="External Verification Details">
            {data.vendor && <EventField label="Vendor">{data.vendor}</EventField>}
            {data.external_vendor_id && (
              <EventField label="Vendor ID">{data.external_vendor_id}</EventField>
            )}
            {data.verification_source && (
              <EventField label="Source">{data.verification_source}</EventField>
            )}
            {data.external_verification_state && (
              <EventField label="State">{data.external_verification_state}</EventField>
            )}
            {data.external_verification_requested_at && (
              <EventField label="Requested At">
                {formatTimestamp(data.external_verification_requested_at)}
              </EventField>
            )}
            {data.external_verification_service_code && (
              <EventField label="Service Code">
                {data.external_verification_service_code}
              </EventField>
            )}
            {data.external_name && (
              <EventField label="Trading Partner">{data.external_name}</EventField>
            )}
            {data.plan_name && <EventField label="Plan Name">{data.plan_name}</EventField>}
            {data.plan_type_description && (
              <EventField label="Plan Type">
                <span className="text-gray-900">{data.plan_type_description}</span>
                {data.plan_type_id && <span className="text-gray-400">({data.plan_type_id})</span>}
              </EventField>
            )}
            {data.subscriber_id && (
              <EventField label="Subscriber ID">{data.subscriber_id}</EventField>
            )}
            {(data.group_name || data.group_number) && (
              <EventField label="Group">
                {[data.group_name, data.group_number].filter(Boolean).join(' / ')}
              </EventField>
            )}
            {(data.pcp_first_name || data.pcp_last_name) && (
              <EventField label="PCP">
                {[data.pcp_first_name, data.pcp_last_name].filter(Boolean).join(' ')}
                {data.pcp_id && <span className="ml-1 text-gray-400">({data.pcp_id})</span>}
              </EventField>
            )}
            {data.effective_on && (
              <EventField label="Effective">{usFormatDateString(data.effective_on)}</EventField>
            )}
            {data.terminate_on && (
              <EventField label="Terminates">{usFormatDateString(data.terminate_on)}</EventField>
            )}
            {data.external_trace_id && (
              <EventField label="Trace ID (EPV)">
                <span className="font-mono text-xs text-gray-900">{data.external_trace_id}</span>
              </EventField>
            )}
            {data.external_verification_trace_id && (
              <EventField label="Trace ID (EV)">
                <span className="font-mono text-xs text-gray-900">
                  {data.external_verification_trace_id}
                </span>
              </EventField>
            )}
            {data.benefit_inquiry_id && (
              <EventField label="270/271 Inquiry ID">
                <span className="font-mono text-xs text-gray-900">{data.benefit_inquiry_id}</span>
              </EventField>
            )}
            {(data.was_discovered || data.is_discovery) && (
              <EventField label="Discovered Policy">Yes</EventField>
            )}
          </EventPanel>

          {data.is_discovery && (
            <EventPanel edge={edge}>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <FontAwesomeIcon icon={faBinoculars} className="text-gray-600" />
                <span>Discovery Details</span>
              </div>
              {data.discovery_purpose && (
                <EventField label="Purpose">{data.discovery_purpose}</EventField>
              )}
              {data.discovery_reduction_status && (
                <EventField label="Reduction Status">{data.discovery_reduction_status}</EventField>
              )}
              {data.discovery_reduction_reason && (
                <EventField label="Reduction Reason">{data.discovery_reduction_reason}</EventField>
              )}
            </EventPanel>
          )}

          {(() => {
            const rejections: { source?: string | null; description?: string | null }[] =
              data.rejections || [];
            const isFailed =
              data.external_verification_state === 'ERROR' ||
              data.external_verification_state === 'REJECTED';
            if (rejections.length === 0 && !isFailed) return null;
            return (
              <EventPanel edge={edge} title="Rejections">
                {rejections.length === 0 ? (
                  <div className="text-sm text-gray-700">
                    No rejection details captured for this failed verification.
                  </div>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {rejections.map((rejection, idx) => (
                      <li key={idx} className="rounded border border-red-100 bg-white p-2">
                        {rejection.source && (
                          <div className="text-xs font-medium text-red-700">{rejection.source}</div>
                        )}
                        <div className="text-gray-900">
                          {rejection.description || 'No description provided'}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </EventPanel>
            );
          })()}

          {(data.subscriber_first_name || data.subscriber_last_name || data.subscriber_dob) && (
            <EventPanel edge={edge} title="Subscriber (Policyholder)">
              {(data.subscriber_first_name || data.subscriber_last_name) && (
                <EventField label="Name">
                  {[data.subscriber_first_name, data.subscriber_last_name]
                    .filter(Boolean)
                    .join(' ')}
                </EventField>
              )}
              {data.subscriber_dob && (
                <EventField label="DOB">{usFormatDateString(data.subscriber_dob)}</EventField>
              )}
            </EventPanel>
          )}

          {(data.person_first_name || data.person_last_name || data.person_dob) && (
            <EventPanel edge={edge} title="Patient (Member)">
              {(data.person_first_name || data.person_last_name) && (
                <EventField label="Name">
                  {[data.person_first_name, data.person_last_name].filter(Boolean).join(' ')}
                </EventField>
              )}
              {data.person_dob && (
                <EventField label="DOB">{usFormatDateString(data.person_dob)}</EventField>
              )}
            </EventPanel>
          )}

          {(data.eligibility_status_code ||
            data.eligibility_status_description ||
            data.coordination_of_benefits_order ||
            data.relationship_to_subscriber ||
            data.is_hospice_care ||
            data.is_medicare_part_a ||
            data.is_medicare_part_b ||
            data.automated_notes) && (
            <EventPanel edge={edge} title="Eligibility">
              {data.eligibility_status_description && (
                <EventField label="Status">
                  <span className="text-gray-900">{data.eligibility_status_description}</span>
                  {data.eligibility_status_code && (
                    <span className="text-gray-400">({data.eligibility_status_code})</span>
                  )}
                </EventField>
              )}
              {data.automated_notes && (
                <EventField label="Automated Notes">{data.automated_notes}</EventField>
              )}
              {data.coordination_of_benefits_order && (
                <EventField label="COB Order">{data.coordination_of_benefits_order}</EventField>
              )}
              {data.relationship_to_subscriber && (
                <EventField label="Relationship">{data.relationship_to_subscriber}</EventField>
              )}
              {data.is_medicare_part_a && (
                <EventPanel isNested title="Medicare Part A">
                  {data.medicare_part_a_effective_on && (
                    <EventField label="Effective">
                      {usFormatDateString(data.medicare_part_a_effective_on)}
                    </EventField>
                  )}
                  {data.medicare_part_a_terminate_on && (
                    <EventField label="Terminates">
                      {usFormatDateString(data.medicare_part_a_terminate_on)}
                    </EventField>
                  )}
                </EventPanel>
              )}
              {data.is_medicare_part_b && (
                <EventPanel isNested title="Medicare Part B">
                  {data.medicare_part_b_effective_on && (
                    <EventField label="Effective">
                      {usFormatDateString(data.medicare_part_b_effective_on)}
                    </EventField>
                  )}
                  {data.medicare_part_b_terminate_on && (
                    <EventField label="Terminates">
                      {usFormatDateString(data.medicare_part_b_terminate_on)}
                    </EventField>
                  )}
                </EventPanel>
              )}
              {data.is_hospice_care && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-900">
                    Hospice Care
                  </span>
                </div>
              )}
            </EventPanel>
          )}

          {data.additional_coverages?.length > 0 && (
            <EventPanel edge={edge} title="Additional Coverages">
              {data.additional_coverages.map(
                (additionalCoverage: ExternalPolicyCoverage, index: number) => (
                  <EventPanel
                    key={index}
                    isNested
                    title={additionalCoverage.plan_name || `Coverage ${index + 1}`}>
                    {additionalCoverage.plan_description && (
                      <EventField label="Plan Description">
                        {additionalCoverage.plan_description}
                      </EventField>
                    )}
                    {additionalCoverage.insurance_type && (
                      <EventField label="Insurance Type">
                        {additionalCoverage.insurance_type}
                      </EventField>
                    )}
                    {additionalCoverage.eligibility_status_description && (
                      <EventField label="Status">
                        {additionalCoverage.eligibility_status_description}
                      </EventField>
                    )}
                    {additionalCoverage.certificate_number && (
                      <EventField label="Certificate #">
                        {additionalCoverage.certificate_number}
                      </EventField>
                    )}
                    {additionalCoverage.effective_on && (
                      <EventField label="Effective">
                        {usFormatDateString(additionalCoverage.effective_on)}
                      </EventField>
                    )}
                    {additionalCoverage.terminate_on && (
                      <EventField label="Terminates">
                        {usFormatDateString(additionalCoverage.terminate_on)}
                      </EventField>
                    )}
                    {!!additionalCoverage.services?.length && (
                      <EventField label="Services">
                        {additionalCoverage.services.join(', ')}
                      </EventField>
                    )}
                    {additionalCoverage.message && (
                      <EventField label="Message">{additionalCoverage.message}</EventField>
                    )}
                  </EventPanel>
                )
              )}
            </EventPanel>
          )}

          {(data.deductible_in_cents !== null ||
            data.deductible_remaining_in_cents !== null ||
            data.office_copay_in_cents !== null ||
            data.hospital_copay_in_cents !== null ||
            data.specialist_copay_in_cents !== null ||
            data.out_of_pocket_max_in_cents !== null ||
            data.out_of_pocket_remaining_in_cents !== null ||
            data.coinsurance_percentage !== null ||
            data.out_of_network_deductible_in_cents !== null ||
            data.out_of_network_out_of_pocket_max_in_cents !== null) && (
            <EventPanel edge={edge} title="Financial">
              {data.deductible_in_cents !== null && (
                <EventField label="Deductible">
                  {currencyInCentsFormatter(data.deductible_in_cents)}
                </EventField>
              )}
              {data.deductible_remaining_in_cents !== null && (
                <EventField label="Deductible Remaining">
                  {currencyInCentsFormatter(data.deductible_remaining_in_cents)}
                </EventField>
              )}
              {data.out_of_pocket_max_in_cents !== null && (
                <EventField label="OOP Max">
                  {currencyInCentsFormatter(data.out_of_pocket_max_in_cents)}
                </EventField>
              )}
              {data.out_of_pocket_remaining_in_cents !== null && (
                <EventField label="OOP Remaining">
                  {currencyInCentsFormatter(data.out_of_pocket_remaining_in_cents)}
                </EventField>
              )}
              {data.coinsurance_percentage !== null && (
                <EventField label="Coinsurance">{data.coinsurance_percentage}%</EventField>
              )}

              {(data.office_copay_in_cents !== null ||
                data.hospital_copay_in_cents !== null ||
                data.specialist_copay_in_cents !== null ||
                data.other_copay_in_cents !== null) && (
                <EventPanel isNested title="Copays">
                  {data.office_copay_in_cents !== null && (
                    <EventField label="Office">
                      {currencyInCentsFormatter(data.office_copay_in_cents)}
                    </EventField>
                  )}
                  {data.hospital_copay_in_cents !== null && (
                    <EventField label="Hospital">
                      {currencyInCentsFormatter(data.hospital_copay_in_cents)}
                    </EventField>
                  )}
                  {data.specialist_copay_in_cents !== null && (
                    <EventField label="Specialist">
                      {currencyInCentsFormatter(data.specialist_copay_in_cents)}
                    </EventField>
                  )}
                  {data.other_copay_in_cents !== null && (
                    <EventField label="Other">
                      {currencyInCentsFormatter(data.other_copay_in_cents)}
                    </EventField>
                  )}
                </EventPanel>
              )}

              {(data.out_of_network_deductible_in_cents !== null ||
                data.out_of_network_deductible_remaining_in_cents !== null ||
                data.out_of_network_out_of_pocket_max_in_cents !== null ||
                data.out_of_network_out_of_pocket_remaining_in_cents !== null ||
                data.out_of_network_coinsurance_percentage !== null) && (
                <EventPanel isNested title="Out of Network">
                  {data.out_of_network_deductible_in_cents !== null && (
                    <EventField label="Deductible">
                      {currencyInCentsFormatter(data.out_of_network_deductible_in_cents)}
                    </EventField>
                  )}
                  {data.out_of_network_deductible_remaining_in_cents !== null && (
                    <EventField label="Deductible Remaining">
                      {currencyInCentsFormatter(data.out_of_network_deductible_remaining_in_cents)}
                    </EventField>
                  )}
                  {data.out_of_network_out_of_pocket_max_in_cents !== null && (
                    <EventField label="OOP Max">
                      {currencyInCentsFormatter(data.out_of_network_out_of_pocket_max_in_cents)}
                    </EventField>
                  )}
                  {data.out_of_network_out_of_pocket_remaining_in_cents !== null && (
                    <EventField label="OOP Remaining">
                      {currencyInCentsFormatter(
                        data.out_of_network_out_of_pocket_remaining_in_cents
                      )}
                    </EventField>
                  )}
                  {data.out_of_network_coinsurance_percentage !== null && (
                    <EventField label="Coinsurance">
                      {data.out_of_network_coinsurance_percentage}%
                    </EventField>
                  )}
                </EventPanel>
              )}
            </EventPanel>
          )}

          {vobLink && (
            <EventPanel edge={edge} title="Linked Records">
              <EventField label="VOB">{vobLink}</EventField>
            </EventPanel>
          )}
        </div>
      );
    }

    // Handle claim submitted
    if (event.event_type === 'claim_submitted') {
      const isResubmitted =
        !!data.last_sent_to_clearinghouse_at &&
        new Date(data.last_sent_to_clearinghouse_at).getTime() !==
          new Date(event.timestamp).getTime();

      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Claim Details">
            {data.cms1500_healthie_id && (
              <EventField label="CMS1500 ID">
                <span className="font-mono text-xs text-gray-900">{data.cms1500_healthie_id}</span>
              </EventField>
            )}
            {data.status && <EventField label="Status">{data.status}</EventField>}
            {data.total_charge_in_cents !== null && data.total_charge_in_cents !== undefined && (
              <EventField label="Total Charge">
                {currencyInCentsFormatter(data.total_charge_in_cents)}
              </EventField>
            )}
            {data.date_of_service && (
              <EventField label="Date of Service">
                {usFormatDateString(data.date_of_service)}
              </EventField>
            )}
            {data.rendering_provider && (
              <EventField label="Rendering Provider">{data.rendering_provider}</EventField>
            )}
            {data.primary_plan_name && (
              <EventField label="Primary Plan Name">{data.primary_plan_name}</EventField>
            )}
            {isResubmitted && (
              <EventField label="Last Sent to Clearing House">
                {formatISOStringToReadableDate(data.last_sent_to_clearinghouse_at)}
              </EventField>
            )}
            {data.resubmission_code && (
              <EventField label="Resubmission Code">{data.resubmission_code}</EventField>
            )}
            {data.orig_ref_number && (
              <EventField label="Original Reference #">{data.orig_ref_number}</EventField>
            )}
            {data.reasons && <EventField label="Reasons">{data.reasons}</EventField>}
          </EventPanel>
        </div>
      );
    }

    // Handle claim status change
    if (event.event_type === 'claim_status_change') {
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Status Transition">
            {data.cms1500_healthie_id && (
              <EventField label="CMS1500 ID">
                <span className="font-mono text-xs text-gray-900">{data.cms1500_healthie_id}</span>
              </EventField>
            )}
            <div className="mt-1 flex items-center gap-2 text-sm">
              {data.old_status && (
                <span className="rounded bg-red-100 px-2 py-1 text-red-800">{data.old_status}</span>
              )}
              <span className="text-gray-400">-&gt;</span>
              {data.new_status && (
                <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                  {data.new_status}
                </span>
              )}
            </div>
          </EventPanel>
        </div>
      );
    }

    // Handle remit received
    if (event.event_type === 'remit_received') {
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Remittance Details">
            {data.cms1500_healthie_id && (
              <EventField label="CMS1500 ID">
                <span className="font-mono text-xs text-gray-900">{data.cms1500_healthie_id}</span>
              </EventField>
            )}
            {data.claim_status_display && (
              <EventField label="Claim Status">{data.claim_status_display}</EventField>
            )}
            {data.payer_classification && (
              <EventField label="Payer Classification">{data.payer_classification}</EventField>
            )}

            <EventPanel isNested title="Amounts">
              {data.charge_amount_in_cents !== null &&
                data.charge_amount_in_cents !== undefined && (
                  <EventField label="Charged">
                    {currencyInCentsFormatter(data.charge_amount_in_cents)}
                  </EventField>
                )}
              {data.paid_amount_in_cents !== null && data.paid_amount_in_cents !== undefined && (
                <EventField label="Paid">
                  <span className="font-semibold text-gray-900">
                    {currencyInCentsFormatter(data.paid_amount_in_cents)}
                  </span>
                </EventField>
              )}
              {data.patient_responsibility_amount_in_cents !== null &&
                data.patient_responsibility_amount_in_cents !== undefined && (
                  <EventField label="Patient Responsibility">
                    {currencyInCentsFormatter(data.patient_responsibility_amount_in_cents)}
                  </EventField>
                )}
            </EventPanel>

            {data.payment && (
              <EventPanel isNested title="Payment">
                {data.payment.payer_name && (
                  <EventField label="Payer">{data.payment.payer_name}</EventField>
                )}
                {data.payment.paid_at && (
                  <EventField label="Paid On">
                    {usFormatDateString(data.payment.paid_at)}
                  </EventField>
                )}
                {data.payment.payment_method && (
                  <EventField label="Method">{data.payment.payment_method}</EventField>
                )}
                {data.payment.check_or_eft_trace_number && (
                  <EventField label="Check #">{data.payment.check_or_eft_trace_number}</EventField>
                )}
                {data.payment.check_amount_in_cents != null && (
                  <EventField label="Check Amount">
                    {currencyInCentsFormatter(data.payment.check_amount_in_cents)}
                  </EventField>
                )}
              </EventPanel>
            )}

            {data.service_lines?.length > 0 && (
              <EventPanel isNested title="Service Lines">
                {data.service_lines.map((serviceLine: RemitServiceLine) => (
                  <RemitServiceLineCard
                    key={serviceLine.id}
                    serviceLine={serviceLine}
                    editable={false}
                  />
                ))}
              </EventPanel>
            )}

            {(data.claim_received_on ||
              data.claim_statement_period_start_on ||
              data.claim_statement_period_end_on) && (
              <>
                {data.claim_received_on && (
                  <EventField label="Received">
                    {usFormatDateString(data.claim_received_on)}
                  </EventField>
                )}
                {data.claim_statement_period_start_on && data.claim_statement_period_end_on && (
                  <EventField label="Statement Period">
                    {usFormatDateString(data.claim_statement_period_start_on)} -{' '}
                    {usFormatDateString(data.claim_statement_period_end_on)}
                  </EventField>
                )}
              </>
            )}

            {data.payer_claim_control_number && (
              <EventField label="Payer Control #">
                <span className="font-mono text-xs text-gray-900">
                  {data.payer_claim_control_number}
                </span>
              </EventField>
            )}
          </EventPanel>
        </div>
      );
    }

    // Handle case rate period start/end
    if (event.event_type === 'case_rate_period') {
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Case Rate Period">
            {data.period_start && (
              <EventField label="Period Start">
                <FieldValue value={data.period_start} fieldKey="period_start" />
              </EventField>
            )}
            {data.period_end && (
              <EventField label="Period End">
                <FieldValue value={data.period_end} fieldKey="period_end" />
              </EventField>
            )}
            {(data.phase_name || data.phase) && (
              <EventField label="Phase">{data.phase_name || `Phase ${data.phase}`}</EventField>
            )}
            {data.num_in_series != null && (
              <EventField label="Number in Series">{data.num_in_series}</EventField>
            )}
          </EventPanel>
        </div>
      );
    }

    // Handle billing item
    if (event.event_type === 'billing_item') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const caseRate = data.case_rate_info as Record<string, any> | null;
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Billing Item Details">
            {data.carrier && <EventField label="Carrier">{data.carrier}</EventField>}
            {data.plan && <EventField label="Plan">{data.plan}</EventField>}
            {data.priority_type && (
              <EventField label="Priority Type">{data.priority_type}</EventField>
            )}
            {data.cpt_code && (
              <EventField label="CPT Code">
                <span className="font-mono text-gray-900">{data.cpt_code}</span>
              </EventField>
            )}
            {data.charge_rate_in_cents !== null && data.charge_rate_in_cents !== undefined && (
              <EventField label="Charge Rate">
                {currencyInCentsFormatter(data.charge_rate_in_cents)}
              </EventField>
            )}
            {data.units != null && <EventField label="Units">{data.units}</EventField>}
            {data.service_type && <EventField label="Service Type">{data.service_type}</EventField>}
            {data.service_class && (
              <EventField label="Service Class">{data.service_class}</EventField>
            )}
            {data.rendering_provider && (
              <EventField label="Rendering Provider">{data.rendering_provider}</EventField>
            )}
            {data.treatability_match && (
              <EventField label="Treatability Match">{data.treatability_match}</EventField>
            )}
            {data.forced_valid && (
              <EventField label="Forced Valid">{formatActionStamp(data.forced_valid)}</EventField>
            )}
            {data.non_billable && (
              <EventPanel isNested title="Non Billable">
                {data.non_billable.reason && (
                  <EventField label="Reason">{data.non_billable.reason}</EventField>
                )}
                <EventField label="Marked">{formatActionStamp(data.non_billable)}</EventField>
              </EventPanel>
            )}
            {data.manual_hold && (
              <EventPanel isNested title="Manual Hold">
                {data.manual_hold.reason && (
                  <EventField label="Reason">{data.manual_hold.reason}</EventField>
                )}
                {data.manual_hold.note && (
                  <EventField label="Note">{data.manual_hold.note}</EventField>
                )}
                <EventField label="Held">{formatActionStamp(data.manual_hold)}</EventField>
              </EventPanel>
            )}
          </EventPanel>

          {caseRate && (
            <EventPanel edge={edge} title="Case Rate">
              {caseRate.period_start && (
                <EventField label="Period Start">
                  <FieldValue value={caseRate.period_start} fieldKey="period_start" />
                </EventField>
              )}
              {caseRate.period_end && (
                <EventField label="Period End">
                  <FieldValue value={caseRate.period_end} fieldKey="period_end" />
                </EventField>
              )}
              {(caseRate.phase_name || caseRate.phase) && (
                <EventField label="Phase">
                  {caseRate.phase_name || `Phase ${caseRate.phase}`}
                </EventField>
              )}
              {caseRate.num_in_series != null && (
                <EventField label="Number in Series">{caseRate.num_in_series}</EventField>
              )}
            </EventPanel>
          )}
        </div>
      );
    }

    // Handle insurance verification (VOB)
    if (event.event_type === 'insurance_verification') {
      const policySnapshot = data.policy_at_verification ?? {};
      const hasPolicySnapshot = Object.values(policySnapshot).some(
        (value) => value !== null && value !== undefined
      );
      const externalVerificationLink = renderEventLink(
        data.external_policy_verification_event_id,
        'View'
      );
      const isResaved =
        !!data.recorded_at &&
        new Date(event.timestamp).getTime() - new Date(data.recorded_at).getTime() > 60_000;

      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Verification Details">
            {data.status && <EventField label="Status">{data.status}</EventField>}
            {data.verification_source && (
              <EventField label="Source">{data.verification_source}</EventField>
            )}
            {data.verified_through && (
              <EventField label="Verified Through">
                {usFormatDateString(data.verified_through)}
              </EventField>
            )}
            {data.deductible_remaining !== null && data.deductible_remaining !== undefined && (
              <EventField label="Deductible Remaining">
                {currencyInCentsFormatter(data.deductible_remaining)}
              </EventField>
            )}
            {data.out_of_pocket_maximum_remaining_in_cents !== null &&
              data.out_of_pocket_maximum_remaining_in_cents !== undefined && (
                <EventField label="OOP Max Remaining">
                  {currencyInCentsFormatter(data.out_of_pocket_maximum_remaining_in_cents)}
                </EventField>
              )}
            {data.service_code && <EventField label="Service Code">{data.service_code}</EventField>}
            {data.estimated_monthly_cost_in_cents != null && (
              <EventField label="Estimated Monthly Cost">
                {currencyInCentsFormatter(data.estimated_monthly_cost_in_cents)}
              </EventField>
            )}
            {data.estimated_initial_month_cost_in_cents != null && (
              <EventField label="Estimated Initial Month Cost">
                {currencyInCentsFormatter(data.estimated_initial_month_cost_in_cents)}
              </EventField>
            )}
            {data.verified_with_identity && (
              <EventField label="Verified With">
                {data.verified_with_identity === 'policyholder' ? 'Policyholder' : 'Member'}
              </EventField>
            )}
            {isResaved && (
              <EventField label="First Recorded">
                {formatISOStringToReadableDate(data.recorded_at)}
              </EventField>
            )}
            {externalVerificationLink && (
              <EventField label="External Verification">{externalVerificationLink}</EventField>
            )}
            {hasPolicySnapshot && (
              <EventPanel
                isNested
                title="Insurance Policy Snapshot"
                titleTooltip="The insurance policy snapshot is a record of the fields the insurance policy had at the time the insurance verification was created.">
                {policySnapshot.carrier && (
                  <EventField label="Carrier">{policySnapshot.carrier}</EventField>
                )}
                {policySnapshot.plan && <EventField label="Plan">{policySnapshot.plan}</EventField>}
                {policySnapshot.subscriber_id && (
                  <EventField label="Subscriber ID">{policySnapshot.subscriber_id}</EventField>
                )}
                {policySnapshot.policy_member_id && (
                  <EventField label="Member ID">{policySnapshot.policy_member_id}</EventField>
                )}
                {policySnapshot.plan_start_date && (
                  <EventField label="Plan Start Date">
                    {formatPlanDate(policySnapshot.plan_start_date)}
                  </EventField>
                )}
                {policySnapshot.plan_end_date && (
                  <EventField label="Plan End Date">
                    {formatPlanDate(policySnapshot.plan_end_date)}
                  </EventField>
                )}
                {policySnapshot.annual_deductible != null && (
                  <EventField label="Annual Deductible">
                    {currencyFormatter(policySnapshot.annual_deductible)}
                  </EventField>
                )}
                {policySnapshot.co_insurance != null && (
                  <EventField label="Co-Insurance">{policySnapshot.co_insurance * 100}%</EventField>
                )}
                {policySnapshot.co_pay != null && (
                  <EventField label="Co-Pay">
                    {currencyFormatter(policySnapshot.co_pay / 100)}
                    {policySnapshot.co_pay_unit && ` x ${policySnapshot.co_pay_unit}`}
                  </EventField>
                )}
                {policySnapshot.out_of_pocket_maximum != null && (
                  <EventField label="Out of Pocket Maximum">
                    {currencyFormatter(policySnapshot.out_of_pocket_maximum / 100)}
                  </EventField>
                )}
              </EventPanel>
            )}
            {data.vob_notes && (
              <EventPanel isNested title="VOB Notes">
                <div className="mt-1 text-xs text-gray-700">
                  {(() => {
                    const notes = data.vob_notes;
                    // HTML or plain string
                    if (typeof notes === 'string') {
                      if (notes.includes('<')) {
                        return <RenderHTML html={notes} className="vob-notes-html [&_p]:my-1" />;
                      }
                      return <span className="whitespace-pre-wrap">{notes}</span>;
                    }
                    return null;
                  })()}
                </div>
              </EventPanel>
            )}
          </EventPanel>
        </div>
      );
    }

    // Handle policy created — backend sends a clean dict of explicit fields
    if (event.event_type === 'policy_created' && data.snapshot) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const snapshot = data.snapshot as Record<string, any>;
      const entries = Object.entries(snapshot).filter(
        ([, v]) => v !== null && v !== undefined && v !== ''
      );
      if (entries.length === 0) return null;
      return (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <EventPanel edge={edge} title="Insurance Policy Values">
            {entries.map(([key, value]) => (
              <EventField
                key={key}
                label={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}>
                {PLAN_DATE_FIELDS.has(key) ? formatPlanDate(value) : String(value)}
              </EventField>
            ))}
          </EventPanel>
        </div>
      );
    }

    // Handle other event types
    return (
      <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
        <EventPanel edge={edge}>
          {Object.entries(data).map(([key, value]) => {
            if (key === 'changes' || key === 'action' || value === null || value === undefined)
              return null;

            return (
              <EventField
                key={key}
                label={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}>
                {PLAN_DATE_FIELDS.has(key) ? (
                  formatPlanDate(value)
                ) : (
                  <FieldValue value={value} fieldKey={key} />
                )}
              </EventField>
            );
          })}
        </EventPanel>
      </div>
    );
  };

  return (
    <div>
      {runs.map((run) => {
        const rail = policyRailColor(run.policyId, policies);
        const policy = policies.find((entry) => entry.id === run.policyId);

        const openingDivider = dividerLabels.get(run.items[0].event.id);

        return (
          <React.Fragment key={run.key}>
            {openingDivider && <TimelineDivider label={openingDivider} />}

            <div className={`mb-8 border-l-4 pl-3 ${rail.border}`}>
              <PolicyBanner policyId={run.policyId} policy={policy} roster={policies} />

              <div className="relative">
                {/* Vertical timeline line — offset right to stay aligned with dot centers */}
                {run.items.length > 1 && (
                  <div
                    className="absolute w-0.5 bg-gray-200"
                    style={{
                      left: 'calc(9.375rem - 1px)',
                      top: '1.375rem',
                      bottom: '1.375rem'
                    }}
                  />
                )}

                {groupCoverages(run.items.map(({ event }) => event)).map((coverages, itemIndex) => {
                  // A group's first event carries the parent verification's payload,
                  // which every coverage repeats.
                  const event = coverages[0];
                  const previous = itemIndex === 0 ? null : run.items[itemIndex - 1].event;
                  const gapDays = previous ? daysBetween(event.timestamp, previous.timestamp) : 0;

                  const eventId = event.id;
                  const isExpanded = expandedEventIds.has(eventId);

                  const display = deriveEventDisplay(event);
                  const displayCarrier = deriveDisplayCarrier(event);
                  const drift = derivePointInTimeDrift(event, policy);
                  const isUnlinked = !policy;

                  return (
                    <React.Fragment key={eventId}>
                      {itemIndex > 0 && dividerLabels.has(event.id) && (
                        <TimelineDivider label={dividerLabels.get(event.id) as string} />
                      )}

                      <div
                        id={`timeline-event-${eventId}`}
                        className="relative flex gap-4"
                        style={{
                          marginTop:
                            itemIndex === 0
                              ? undefined
                              : `${1.5 + getAdditionalSpacing(gapDays)}rem`
                        }}>
                        <div className="w-28 flex-shrink-0 pt-3 text-right">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatEventDay(event)}
                          </div>
                          {!calendarDateOf(event) && (
                            <div className="text-xs text-gray-500">
                              {formatEventTime(event.timestamp)}
                            </div>
                          )}
                        </div>

                        {/* Timeline dot with icon */}
                        <div
                          className={`relative z-10 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${display.palette.dot} shadow-md`}>
                          <FontAwesomeIcon
                            icon={display.icon}
                            className={`text-lg ${display.iconColor}`}
                          />
                        </div>

                        {/* Event card */}
                        <Card
                          padding="none"
                          className={`flex-1 ${
                            eventId === focusedEventId
                              ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-50'
                              : ''
                          }`}>
                          <button
                            onClick={() => toggleExpanded(eventId)}
                            className="w-full px-4 py-3 text-left transition-colors hover:bg-gray-50">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="font-semibold text-gray-900">{display.label}</h3>
                                  {display.status && (
                                    <Pill color={display.status.color} size="sm">
                                      {display.status.text}
                                    </Pill>
                                  )}
                                  {event.policy_deleted_at && (
                                    <FontAwesomeIcon
                                      icon={faTrashCan}
                                      className="text-sm text-red-600"
                                      title={`Policy Deleted on ${new Date(event.policy_deleted_at).toLocaleDateString()}`}
                                    />
                                  )}
                                  {coverages.length > 1 && (
                                    <span className="rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-800">
                                      {coverages.length} coverages
                                    </span>
                                  )}
                                  {display.badges.map((badge) => (
                                    <span
                                      key={badge.text}
                                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge.style}`}>
                                      {badge.text}
                                    </span>
                                  ))}
                                  {drift.carrier && (
                                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                                      was {drift.carrier}
                                    </span>
                                  )}
                                  {drift.priority && (
                                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                                      was {drift.priority.toUpperCase()}
                                    </span>
                                  )}
                                  {isUnlinked && (
                                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                      {displayCarrier}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 text-sm text-gray-600">
                                  {event.actor_email || 'system'}
                                </div>
                              </div>
                              <svg
                                className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="border-t border-gray-100 px-4 pb-3">
                              {coverages.map((coverage, coverageIndex) => (
                                <div key={coverage.id}>
                                  {coverages.length > 1 && (
                                    <div className="mt-3 text-xs font-semibold text-gray-900">
                                      Coverage {coverageIndex + 1} of {coverages.length}
                                    </div>
                                  )}
                                  {renderEventData(coverage)}
                                </div>
                              ))}
                              <div className="mt-2 flex items-center justify-between">
                                <CopyContent
                                  content={eventPermalink(eventId)}
                                  className="text-xs text-gray-500">
                                  Copy link to this event
                                </CopyContent>
                                <Debug object={event} />
                              </div>
                            </div>
                          )}
                        </Card>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default TimelineRow;
